import React from 'react';
import classNames from 'classnames';

import { TimesCircleAltIcon } from '../../icons/react';

interface RCChipProps {
  /** RCChips are simpler than RCButtons and expect only a toggled state. */
  selected?: boolean;
  /** Standard change handler; this component doesn't track its own state. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** We allow any markup here for overall consistency but this should likely remain a simple string */
  children: React.ReactNode;
  /**
   * These sizes are set up to replicate our RCButton sizes.
   * `mini` RCChips don't render a close icon and are intended for use in a group of selectable items, e.g. suggestions below an input field.
  */
  size?: 'small' | 'mini';
}

/**
 * A special simplified button control for use when the user is taking action _within_ the context of the page they're on.
 *
 * They're often used in groups; in those cases, put them inside the `RCChip.Collection` wrapper component
 * to standardize spacing & wrapping.
 */
export function RCChip({
  selected = false,
  onClick = undefined,
  size = 'small',
  children,
}: RCChipProps) {
  const classes = classNames(
    'rc-chip',
    {
      'rc-chip--small': size === 'small',
      'rc-chip--mini': size === 'mini',
    }
  );

  return (
    <button
      className={classes}
      type="button"
      aria-pressed={selected}
      onClick={onClick}
    >
      {children}
      {selected &&
        <div className="rc-chip__remove">
          <TimesCircleAltIcon />
        </div>
      }
    </button>
  );
}

// Wrapper container to standardize spacing & wrapping.

interface RCChipCollectionProps {
  nowrap?: 'tablet',
  children,
}

function RCChipCollection({
  nowrap,
  children,
}: RCChipCollectionProps) {
  const classes = classNames(
    'd-flex fx-wrap gap-2 tablet-d-block pr-10',
    {
      'tablet-ws-no-wrap': nowrap === 'tablet',
    }
  )

  return (
    <div className={classes}>
      {children}
    </div>
  )
}

RCChip.Collection = RCChipCollection;
